import { BaseResource } from './base';
import { RequestOptions } from '../types';
import {
  BulkTemplate,
  CreateBulkTemplateDto,
  UpdateBulkTemplateDto,
  BulkMessageVariable,
  SendBulkMessagesDto,
  BulkMessageResultDto,
  BulkLogsResponseDto,
  GenerateAIMessageDto,
  GenerateAIMessageResponseDto,
} from '../models/bulk-messaging';

export class BulkMessagingResource extends BaseResource {
  private basePath = 'whatsapp/bulk-messaging';
  private templatesPath = 'whatsapp/bulk-templates';

  async createTemplate(
    data: CreateBulkTemplateDto,
    options?: RequestOptions,
  ): Promise<BulkTemplate> {
    return this.client.post<BulkTemplate>(this.templatesPath, data, options);
  }

  async getTemplates(options?: RequestOptions): Promise<BulkTemplate[]> {
    return this.client.get<BulkTemplate[]>(this.templatesPath, undefined, options);
  }

  async getTemplate(id: string, options?: RequestOptions): Promise<BulkTemplate> {
    return this.client.get<BulkTemplate>(`${this.templatesPath}/${id}`, undefined, options);
  }

  async updateTemplate(
    id: string,
    data: UpdateBulkTemplateDto,
    options?: RequestOptions,
  ): Promise<BulkTemplate> {
    return this.client.put<BulkTemplate>(`${this.templatesPath}/${id}`, data, options);
  }

  async deleteTemplate(id: string, options?: RequestOptions): Promise<void> {
    return this.client.delete<void>(`${this.templatesPath}/${id}`, options);
  }

  async getAvailableVariables(options?: RequestOptions): Promise<BulkMessageVariable[]> {
    return this.client.get<BulkMessageVariable[]>(
      `${this.basePath}/available-variables`,
      undefined,
      options,
    );
  }

  /**
   * Generate AI message with streaming support
   * Note: This proxies to the agent endpoint for streaming responses
   */
  async generateAIMessage(
    data: GenerateAIMessageDto,
    options?: RequestOptions,
  ): Promise<GenerateAIMessageResponseDto> {
    return this.client.post<GenerateAIMessageResponseDto>(
      `${this.basePath}/generate-ai`,
      data,
      options,
    );
  }

  /**
   * Send bulk messages to multiple clients
   * Note: SDK sends clientIds, API fetches full client and gym data before sending to agent
   */
  async send(data: SendBulkMessagesDto, options?: RequestOptions): Promise<BulkMessageResultDto> {
    return this.client.post<BulkMessageResultDto>(`${this.basePath}/send`, data, options);
  }

  /**
   * Get logs for a bulk message send operation
   */
  async getLogs(sendId: string, options?: RequestOptions): Promise<BulkLogsResponseDto> {
    return this.client.get<BulkLogsResponseDto>(`${this.basePath}/logs`, { sendId }, options);
  }
}
